const optionIdCandidateKeys = [
    "id",
    "optionId",
    "option_id",
    "uuid",
    "_id",
    "slug",
    "code",
    "key",
];

const optionNameCandidateKeys = ["name", "optionName", "title", "label", "displayName"];

const optionGroupCandidateKeys = [
    "group",
    "groupName",
    "group_name",
    "category",
    "categoryName",
    "category_name",
    "optionGroup",
    "option_group",
    "section",
];

const optionActiveCandidateKeys = ["active", "isActive", "enabled", "available", "status", "state"];

const truthyValues = [
    "true",
    "1",
    "available",
    "in_stock",
    "instock",
    "active",
    "on",
];

const falsyValues = [
    "false",
    "0",
    "unavailable",
    "out_of_stock",
    "inactive",
    "off",
    "sold_out",
    "soldout",
];

const parseBooleanLike = (value: any): boolean | undefined => {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "number") {
        if (Number.isNaN(value)) {
            return undefined;
        }
        return value !== 0;
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized.length === 0) {
            return undefined;
        }

        if (truthyValues.includes(normalized)) {
            return true;
        }

        if (falsyValues.includes(normalized)) {
            return false;
        }
    }

    return undefined;
};

export const toComparableMenuOptionId = (
    value: string | number | undefined | null
): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }

    return String(value);
};

export const getMenuOptionId = (option: any): string | number | undefined => {
    if (!option || typeof option !== "object") {
        return undefined;
    }

    for (const key of optionIdCandidateKeys) {
        const value = (option as Record<string, any>)[key];
        if (value !== undefined && value !== null && value !== "") {
            return value as string | number;
        }
    }

    return undefined;
};

export const getMenuOptionName = (option: any): string => {
    if (!option || typeof option !== "object") {
        return "";
    }

    for (const key of optionNameCandidateKeys) {
        const value = (option as Record<string, any>)[key];
        if (typeof value === "string" && value.trim().length > 0) {
            return value.trim();
        }
    }

    if (typeof (option as any).name === "string") {
        return (option as any).name;
    }

    return "";
};

export const getMenuOptionSelectionType = (option: any): "single" | "multiple" => {
    if (!option || typeof option !== "object") {
        return "single";
    }

    const value =
        (option as Record<string, any>).selection_type ??
        (option as Record<string, any>).selectionType ??
        (option as Record<string, any>).select_type ??
        (option as Record<string, any>).selectType;

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "multiple") {
            return "multiple";
        }
    }

    if (typeof value === "number") {
        if (value === 2) {
            return "multiple";
        }
    }

    return "single";
};

export const getMenuOptionGroupName = (option: any): string | undefined => {
    if (!option || typeof option !== "object") {
        return undefined;
    }

    for (const key of optionGroupCandidateKeys) {
        const value = (option as Record<string, any>)[key];
        if (typeof value === "string" && value.trim().length > 0) {
            return value.trim();
        }
    }

    return undefined;
};

export const getMenuOptionIsActive = (option: any): boolean => {
    if (!option || typeof option !== "object") {
        return true;
    }

    for (const key of optionActiveCandidateKeys) {
        const value = (option as Record<string, any>)[key];
        const parsed = parseBooleanLike(value);
        if (parsed !== undefined) {
            return parsed;
        }

        if (typeof value === "string") {
            const normalized = value.trim().toLowerCase();
            if (["active", "available", "enabled"].includes(normalized)) {
                return true;
            }
            if (["inactive", "disabled", "unavailable"].includes(normalized)) {
                return false;
            }
        }
    }

    return true;
};

export const formatMenuOptionLabel = (option: any): string => {
    const name = getMenuOptionName(option);
    const selectionType = getMenuOptionSelectionType(option);
    const typeLabel = selectionType === "multiple" ? "Chọn nhiều" : "Chọn 1";

    return `${name} (${typeLabel})`;
};

const optionArrayCandidateKeys = [
    "options",
    "menuOptions",
    "menu_options",
    "optionGroups",
    "option_groups",
    "groups",
];

export const extractMenuOptionListFromMenu = (menu: any): any[] => {
    if (!menu || typeof menu !== "object") {
        return [];
    }

    for (const key of optionArrayCandidateKeys) {
        const value = (menu as Record<string, any>)[key];
        if (Array.isArray(value)) {
            return value;
        }
    }

    return [];
};

const optionIdArrayCandidateKeys = [
    "optionIds",
    "option_ids",
    "menuOptionIds",
    "menu_option_ids",
    "optionIdList",
    "option_id_list",
];

export const extractMenuOptionIdsFromMenu = (menu: any): (string | number)[] => {
    const result: (string | number)[] = [];
    const seen = new Set<string>();

    const addId = (id: string | number | undefined | null) => {
        const comparable = toComparableMenuOptionId(id ?? null);
        if (!comparable || seen.has(comparable)) {
            return;
        }
        seen.add(comparable);
        if (id !== undefined && id !== null) {
            result.push(id);
        }
    };

    if (!menu || typeof menu !== "object") {
        return result;
    }

    for (const key of optionIdArrayCandidateKeys) {
        const value = (menu as Record<string, any>)[key];
        if (Array.isArray(value)) {
            value.forEach((item) => addId(item as any));
        }
    }

    const optionList = extractMenuOptionListFromMenu(menu);
    optionList.forEach((option) => {
        addId(getMenuOptionId(option));
    });

    return result;
};

export interface MenuOptionSelectChoice {
    value: string;
    label: string;
    option: any;
    rawId: string | number | undefined;
    group?: string;
}

export const buildMenuOptionChoices = (options: any[]): MenuOptionSelectChoice[] => {
    if (!Array.isArray(options)) {
        return [];
    }

    return options
        .map((option) => {
            const rawId = getMenuOptionId(option);
            const comparableId = toComparableMenuOptionId(rawId ?? null);
            if (!comparableId) {
                return null;
            }

            return {
                value: comparableId,
                label: formatMenuOptionLabel(option),
                option,
                rawId,
                group: getMenuOptionGroupName(option),
            } as MenuOptionSelectChoice;
        })
        .filter((choice): choice is MenuOptionSelectChoice => Boolean(choice));
};

export const createMenuOptionLookup = (options: any[]) => {
    const map = new Map<
        string,
        {
            option: any;
            rawId: string | number | undefined;
        }
    >();

    if (!Array.isArray(options)) {
        return map;
    }

    options.forEach((option) => {
        const rawId = getMenuOptionId(option);
        const comparableId = toComparableMenuOptionId(rawId ?? null);
        if (!comparableId) {
            return;
        }

        map.set(comparableId, { option, rawId });
    });

    return map;
};

export interface MenuOptionDisplayItem {
    id: string;
    name: string;
}

export const getMenuOptionDisplayItems = (
    menu: any,
    lookup: Map<string, { option: any; rawId: string | number | undefined }>
): MenuOptionDisplayItem[] => {
    const items: MenuOptionDisplayItem[] = [];
    const seen = new Set<string>();

    const pushItem = (idValue: string | number | undefined, option?: any) => {
        const comparableId = toComparableMenuOptionId(idValue ?? null);
        if (!comparableId || seen.has(comparableId)) {
            return;
        }

        let name = "";
        if (option) {
            name = getMenuOptionName(option);
        } else if (lookup.has(comparableId)) {
            name = getMenuOptionName(lookup.get(comparableId)?.option);
        }

        if (!name) {
            name = comparableId;
        }

        seen.add(comparableId);
        items.push({
            id: comparableId,
            name,
        });
    };

    const directOptions = extractMenuOptionListFromMenu(menu);
    directOptions.forEach((option) => {
        const optionId = getMenuOptionId(option);
        pushItem(optionId, option);
    });

    const idList = extractMenuOptionIdsFromMenu(menu);
    idList.forEach((id) => {
        pushItem(id, lookup.get(toComparableMenuOptionId(id) ?? "")?.option);
    });

    return items;
};
